/**
 * How a batch's status is named and coloured, in one place.
 *
 * Shared by the list badge, the detail phase tracker and the brew-day header,
 * so a `conditioning` batch reads identically wherever it is shown.
 *
 * The tracker itself moved out to `components/stage-stepper.tsx` (D-BS5): a
 * stepper is a stepper, and the blend's stages want the same control. What
 * stays here is the batch's own vocabulary — its labels, its badge tones, and
 * the two captions the track is read by — behind the name every caller already
 * imports.
 */

import type { BatchStatus } from '@beerwright/commands';
import { BATCH_PROGRESS_STATUSES } from '@beerwright/commands';
import { StageStepper } from '@/components/stage-stepper';
import { Badge } from '@/components/ui/badge';

/** Human-readable names. */
export const BATCH_STATUS_LABELS: Readonly<Record<BatchStatus, string>> = {
  planned: 'Planned',
  'brew-day': 'Brew day',
  fermenting: 'Fermenting',
  conditioning: 'Conditioning',
  packaged: 'Packaged',
  archived: 'Archived',
};

/**
 * The badge's tone.
 *
 * Only `brew-day` gets the primary colour: it is the one status that means
 * "something is happening right now, on a burner, unattended". Everything else
 * is a resting state and reads as an outline, and `archived` recedes further.
 */
const STATUS_VARIANT: Readonly<Record<BatchStatus, 'default' | 'secondary' | 'outline' | 'muted'>> =
  {
    planned: 'outline',
    'brew-day': 'default',
    fermenting: 'secondary',
    conditioning: 'secondary',
    packaged: 'outline',
    archived: 'muted',
  };

export function BatchStatusBadge({
  status,
  className,
}: {
  readonly status: BatchStatus;
  readonly className?: string;
}) {
  return (
    <Badge variant={STATUS_VARIANT[status]} className={className}>
      {BATCH_STATUS_LABELS[status]}
    </Badge>
  );
}

export interface BatchStatusStepperProps {
  readonly status: BatchStatus;
  readonly onChange: (status: BatchStatus) => void;
  readonly className?: string;
}

/**
 * The brewing pipeline, clickable in both directions.
 *
 * `StageStepper` under the name every caller already has: the control is
 * generic, but "Phase" and the five brewing stages are the batch's, and a
 * detail page should not have to restate them to draw its own tracker.
 */
export function BatchStatusStepper({ status, onChange, className }: BatchStatusStepperProps) {
  return (
    <StageStepper
      stages={BATCH_PROGRESS_STATUSES}
      labels={BATCH_STATUS_LABELS}
      current={status}
      onChange={onChange}
      caption="Phase"
      trackLabel="Brewing phase"
      {...(status === 'archived' ? { note: ARCHIVED_NOTE } : {})}
      className={className}
    />
  );
}

/**
 * What an archived batch says about itself, beside the unlit track.
 *
 * `archived` is the one status this page cannot explain by drawing it: nothing
 * on the track is lit, and the header's brew-day button is disabled with no
 * room to say why. A `title` was the first answer and it was a desktop one: a
 * phone has no pointer to hover, so it read as a dead button and no reason. The
 * sentence goes where the stage's name already is, which is where somebody
 * looking at a stepper that has gone dark is already looking.
 *
 * **It offers the track before the list, because the track is both nearer and
 * lossless.** The stepper is fully live while a batch is filed — every step is
 * an enabled button wired to `batch.setStatus` — so one tap on the phase the
 * beer is actually in takes it out of the drawer *and* puts it where it belongs.
 * The list's Unarchive is a second page and lands the batch on `packaged`
 * whatever it was, because nothing records where it came from. Naming that
 * route first would have sent a brewer the long way round to lose the one fact
 * the short way keeps. Both are still offered: the list is where somebody who
 * archived the batch by mistake is looking (PLAN-UX-PASS B3).
 */
const ARCHIVED_NOTE =
  'You filed this one away. Pick a phase below to take it back out, or unarchive it from the batch list.';
